import { ComponentProps, useMemo, useRef } from 'react';
import { css } from '@emotion/react';
import { Row } from 'react-table';
import { useTranslation } from '@frontend/i18n';
import { LocationFilterMenu } from '@frontend/location-filter-menu';
import { SettingsTenantLocation, usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { TableColumnConfig, TableProps, Chip, Table, TrashIcon, TableRowActions } from '@frontend/design-system';

export const IndexTable = <T extends object, C extends TableColumnConfig<T>[]>({
  tenantLocation,
  idAccessor,
  labelsAccessor,
  labelsColumnPosition = 1,
  nameAccessor,
  filteredLocations,
  setFilteredLocations,
  pageIndex,
  setPageIndex,
  rowsPerPage,
  setRowsPerPage,
  DeleteIconButton,
  onRowClick,
  canDeleteRow,
  tableConfig,
  trackingIdBase,
  showDelete = true,
  rowActions = [],
  keepPreviousDataWhileLoading = false,
  locationsColumnConfig,
}: {
  tenantLocation: SettingsTenantLocation;
  idAccessor: (data: T) => string;
  labelsAccessor: (data: T) => string[];
  labelsColumnPosition?: number;
  nameAccessor: (data: T) => string;
  filteredLocations: string[];
  setFilteredLocations: (locations: string[]) => void;
  pageIndex?: number;
  setPageIndex?: (index: number) => void;
  rowsPerPage?: number;
  setRowsPerPage?: (size: number) => void;
  DeleteIconButton?: React.FC<{ name: string; id: string; phoneTenantId: string; disabled?: boolean }>;
  keepPreviousDataWhileLoading?: boolean;
  locationsColumnConfig?: Partial<TableColumnConfig<T>>;
  /**
   * This prop is unique in that we single it out of the tableConfig prop so allow consumers to modify just the row click behavior within the `rowActions` field
   */
  onRowClick?: (row: T) => void;
  canDeleteRow?: (row: T) => boolean;
  tableConfig: TableProps<T, C>;
  trackingIdBase?: string;
  showDelete?: boolean;
  rowActions?: TableRowActions<T>['actions'];
}) => {
  const { t } = useTranslation('phone');
  const { getScopeName } = useAppScopeStore();
  const isUnify = tenantLocation && tenantLocation.locationType === 'unify';
  // Saves the page to restore after clearing global search.
  const searchStartPageIndexRef = useRef<number | null>(null);

  const config: TableProps<T, C> = useMemo(() => {
    const basePaginationConfig = tableConfig.clientPaginationConfig;
    const baseGlobalSearchConfig = tableConfig.globalSearchConfig;
    const clientPaginationConfig = basePaginationConfig
      ? {
          ...basePaginationConfig,
          initialPage: pageIndex,
          defaultRowsPerPage: rowsPerPage,
          onPaginationChange: (newPage: number) => {
            setPageIndex?.(newPage);
            basePaginationConfig.onPaginationChange?.(newPage);
          },
          onRowsPerPageChange: (newSize: number) => {
            setRowsPerPage?.(newSize);
            basePaginationConfig.onRowsPerPageChange?.(newSize);
          },
        }
      : undefined;
    // Show search results from page 1, then restore the page where search began.
    const globalSearchConfig =
      tableConfig.hasGlobalSearch !== false
        ? {
            ...baseGlobalSearchConfig,
            onSearchChange: (searchValue: string) => {
              const hasSearchValue = searchValue.trim().length > 0;

              if (hasSearchValue) {
                searchStartPageIndexRef.current ??= pageIndex ?? 0;
                setPageIndex?.(0);
              } else {
                const previousPageIndex = searchStartPageIndexRef.current;
                searchStartPageIndexRef.current = null;
                if (previousPageIndex !== null) {
                  setPageIndex?.(previousPageIndex);
                }
              }

              baseGlobalSearchConfig?.onSearchChange?.(searchValue);
            },
          }
        : undefined;
    const defaultRowActions: TableRowActions<T>['actions'] | undefined = !showDelete
      ? undefined
      : showDelete && DeleteIconButton
      ? [
          {
            label: t('Delete'),
            Icon: TrashIcon,
            disabled: (row) => !canDeleteRow?.(row),
            renderCustom: (_getItemProps, _index, _close, _isSingleAction, rowData) => {
              if (!tenantLocation.phoneTenantId) {
                return null;
              }
              return (
                <DeleteIconButton
                  key={`$delete-${idAccessor(rowData)}`}
                  name={nameAccessor(rowData)}
                  id={idAccessor(rowData)}
                  phoneTenantId={tenantLocation.phoneTenantId}
                  disabled={!canDeleteRow?.(rowData)}
                />
              );
            },
          },
          ...rowActions,
        ]
      : rowActions;
    // This is the common config we want all tables to share
    const defaultConfig = {
      fullHeight: true,
      uniqueRowId: idAccessor,
      keepPreviousDataWhileLoading,
      rowActions: {
        onRowClick,
        actions: defaultRowActions,
      },
      hasGlobalSearch: true,
      customToolbarRender:
        tenantLocation?.locationType !== 'single'
          ? () => (
              <PhoneSettingsScopedLocationFilterMenu
                selectedOptions={filteredLocations}
                onChange={setFilteredLocations}
                trackingIdBase={trackingIdBase}
              />
            )
          : undefined,
      isPaginated: true,
      wrapperStyle: css`
        .table-toolbar {
          padding: 0;
          display: grid;
          gap: ${theme.spacing(1)};
          grid-template-columns: auto 1fr auto;
          > div:first-of-type {
            grid-column: 1;
          }
          > div:last-of-type {
            margin: 0;
            width: auto;
            grid-column: 3;
          }
        }
      `,
      ...tableConfig,
      clientPaginationConfig,
      globalSearchConfig,
      manualPaginationConfig: undefined,
    };

    // This is the column config for the labels column
    const labelsColumnConfig = {
      id: 'labels',
      Header: 'Locations',
      accessor: (data: T) => {
        const locationIds = labelsAccessor(data);
        if (!locationIds) {
          return;
        }
        if (locationIds.length === 1) {
          return { label: getScopeName(locationIds[0]), type: 'single' };
        } else if (locationIds.length > 1) {
          return { label: t('{{count}} locations', { count: locationIds.length }), type: 'multi' };
        } else {
          return { label: tenantLocation.name, type: 'multi' };
        }
      },
      cellRenderer: (data: { label: string; type: 'multi' | 'single' }) =>
        data.type === 'multi' ? (
          <Chip.MultiChip>{data.label}</Chip.MultiChip>
        ) : (
          <Chip.SingleChip>{data.label}</Chip.SingleChip>
        ),
      width: 150,
      omit: !isUnify,
      sortType: (a: Row<T>, b: Row<T>) => {
        return a.values?.labels?.label.localeCompare(b.values?.labels?.label);
      },
      ...locationsColumnConfig,
    } as unknown as TableColumnConfig<T>;

    // Insert the labels column config at the specified position
    const config = {
      ...defaultConfig,
      colConfig: [
        ...defaultConfig.colConfig.slice(0, labelsColumnPosition),
        labelsColumnConfig,
        // Not replacing items, so we don't need to +1 here
        ...defaultConfig.colConfig.slice(labelsColumnPosition),
      ],
    };

    return config as TableProps<T, C>;
  }, [
    tableConfig,
    canDeleteRow,
    idAccessor,
    labelsAccessor,
    nameAccessor,
    onRowClick,
    pageIndex,
    rowsPerPage,
    setFilteredLocations,
    setPageIndex,
    setRowsPerPage,
    tableConfig,
    tenantLocation,
    t,
    labelsColumnPosition,
  ]);

  return <Table {...config} />;
};

export const PhoneSettingsScopedLocationFilterMenu = ({
  selectedOptions,
  onChange,
  trackingIdBase,
}: Pick<ComponentProps<typeof LocationFilterMenu>, 'selectedOptions' | 'onChange' | 'trackingIdBase'>) => {
  const { accessibleLocationData } = useAppScopeStore();
  const { globalAvailableLocationIds } = usePhoneSettingsShallowStore('globalAvailableLocationIds');
  const locallyAvailableLocations =
    globalAvailableLocationIds?.map((id) => accessibleLocationData[id]).filter(Boolean) ?? [];

  return (
    <LocationFilterMenu
      locations={locallyAvailableLocations}
      selectedOptions={selectedOptions}
      onChange={onChange}
      trackingIdBase={trackingIdBase}
    />
  );
};
